"use client";

import LoadingOverlay from "./LoadingOverlay";
import React from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";

import PetDetails from "@/components/PetDetails";
import ProductVariantSelection from "@/components/ProductVariantSelection";
import ProductCustomizationOptions from "@/components/ProductCustomizationOptions";
import ContactDetailsForm from "@/components/ContactDetailsForm";
import ConfirmationPage from "@/components/ConfirmationPage";
import { FunnelState, ProductVariant } from "@/hooks/useFunnelState";
import { OfferResponse } from "@/types/simpego-api";
import { BaseCoverageId } from "@/config/coverages";
import { CoverageState } from "@/utils/coveragePayload";
import { ApplicationAnswers } from "@/components/contact-details/ApplicationQuestionsSection";

interface FunnelStepRendererProps {
  funnelState: FunnelState;
  getLocalizedPath: (pathKey: string) => string;
  isLoadingPrice: boolean;
  fetchBundleRecommendations: (
    petTypeArg: "cat" | "dog",
    petNameArg: string,
    breedArg: string | null,
    birthDateArg: Date | undefined,
    selectedBreedTypeArg: "purebred" | "crossbreed" | "mixed",
    genderArg: "male" | "female" | "unknown",
    isCastratedArg: boolean,
    isHouseCatArg: boolean,
    selectedDogSizeArg: "small" | "large" | null,
    coverageSelectionsArg: Partial<Record<BaseCoverageId, boolean>>,
    locale: string,
    agentIdArg: string | null,
    startOfInsuranceArg: Date
  ) => Promise<{ S: OfferResponse; M: OfferResponse; L: OfferResponse } | null>;
  currentApiPrice: number | null;
  currentOffer: OfferResponse | null;
  coveragePremiums: Partial<Record<BaseCoverageId, number>>;
  declinedSubs: Partial<Record<BaseCoverageId, Record<string, boolean>>>;
  coverageUnderwritingReasons: Partial<Record<BaseCoverageId, string>>;
  declinedCoverages: Partial<Record<BaseCoverageId, string>>;
  fetchCoverageRecommendation: (
    targetCoverageId: BaseCoverageId,
    currentCoverageState: CoverageState
  ) => Promise<CoverageState | null>;
  underwritingFailed: boolean;
  underwritingReason: string | null;
  handlePetDetailsSubmitAndNavigate: (
    submittedPetName: string,
    selectedBreed: string,
    submittedBreedLabel: string | null,
    submittedBirthDate: Date | undefined,
    submittedSelectedBreedType: "purebred" | "crossbreed" | "mixed",
    submittedGender: "male" | "female" | "unknown",
    submittedIsCastrated: boolean,
    submittedIsHouseCat: boolean,
    submittedSelectedDogSize: "small" | "large" | null,
    submittedCoverageSelections: Partial<Record<BaseCoverageId, boolean>>,
    submittedStartOfInsurance: Date,
    submittedChipNumber: string | null
  ) => Promise<void>;
  handleSelectProductVariant: (product: ProductVariant) => void;
  handleSelectProductVariantAndSkipToContact: (product: ProductVariant) => void;
  handleProductVariantSelectionAndNavigate: (product: ProductVariant) => void;
  handlePriceCustomizationSubmitAndNavigate: (
    product: ProductVariant | null,
    price: number,
    coverageDeductibles: Partial<Record<BaseCoverageId, number>>,
    subCoverageSelections: Partial<Record<BaseCoverageId, Record<string, boolean>>>,
    coverageSumInsured: Partial<Record<BaseCoverageId, number>>,
    dailyTermination: boolean,
    interval: 1 | 2 | 4 | 12,
    method: "credit_card" | "ESR",
    startDate: Date
  ) => void;
  handleCreateApplication: (
    firstName: string,
    lastName: string,
    email: string,
    phone: string,
    street: string,
    housenumber: string,
    city: string,
    zipCode: string,
    country: string
  ) => Promise<void>;
  handleContactDetailsContinue: (
    firstName: string,
    lastName: string,
    email: string,
    phone: string,
    street: string,
    housenumber: string,
    city: string,
    zipCode: string,
    country: string,
    birthdate: Date | undefined,
    gender: "male" | "female" | ""
  ) => void;
  handleContactDetailsFieldBlur: (
    firstName: string,
    lastName: string,
    email: string,
    phone: string,
    street: string,
    housenumber: string,
    city: string,
    zipCode: string,
    country: string,
    birthdate: Date | undefined,
    gender: "male" | "female" | ""
  ) => void;
  isSubmittingOffer: boolean;
  goToHome: () => void;
  goToPetDetails: () => void;
  goToProductVariantSelection: () => void;
  handleBackFromProductCustomization: () => void;
  handleBackFromContactDetails: () => void;
  goToProductCustomizationOptions: (options?: { scrollTo?: "payment" }) => void;
  goToContactDetails: () => void;
  goToConfirmation: () => void;
  setSelectedProduct: (product: ProductVariant | null) => void;
  setChipNumber: (value: string | null) => void;
  changeCoverageSelectionsOnComparison: (next: Partial<Record<BaseCoverageId, boolean>>) => void;
  setCoverageSelections: (next: Partial<Record<BaseCoverageId, boolean>>) => void;
  setSubCoverageSelections: (next: Partial<Record<BaseCoverageId, Record<string, boolean>>>) => void;
  setSubCoverageSumInsured: (next: Partial<Record<BaseCoverageId, Record<string, number>>>) => void;
  setCoverageDeductibles: (next: Partial<Record<BaseCoverageId, number>>) => void;
  setCoverageDeductiblesOtherBand: (next: Partial<Record<BaseCoverageId, number>>) => void;
  setCoverageSumInsured: (next: Partial<Record<BaseCoverageId, number>>) => void;
  setCoverageSumInsuredPerCase: (next: Partial<Record<BaseCoverageId, string>>) => void;
  setEmergencyCareSumInsuredPerDay: (value: "50" | "100" | null) => void;
  setAnnualDeductible: (value: number | null) => void;
  setDailyTermination: (value: boolean) => void;
  setBundleRecommendations: (data: { S: OfferResponse; M: OfferResponse; L: OfferResponse } | null) => void;
  setPaymentInterval: (interval: 1 | 2 | 4 | 12) => void;
  setPaymentMethod: (method: "credit_card" | "ESR") => void;
  setStartOfInsurance: (date: Date) => void;
  setFirstDue: (date: Date | null) => void;
  onPetTypeChange: (petType: "cat" | "dog") => void;
  setDispatchContract: (v: "customer" | "customer_and_agent" | "agent") => void;
  setDispatchInvoice: (v: "customer" | "customer_and_agent" | "agent") => void;
  setDispatchReminder: (v: "customer" | "customer_and_agent" | "agent") => void;
  setDispatchEmail: (v: "customer" | "customer_and_agent" | "agent") => void;
  setAgentDispatchEmail: (email: string | null) => void;
  setCampaignCode: (code: string) => void;
  applicationAnswers: ApplicationAnswers;
  setApplicationAnswers: (answers: ApplicationAnswers) => void;
  setConsultationProtocolDocument: (documentId: number, filename: string) => void;
  clearConsultationProtocolDocument: () => void;
  setConsultationProtocolPending: (base64File: string, filename: string) => void;
  clearConsultationProtocolPending: () => void;
  setBundlePrices: (prices: { S: number; M: number; L: number } | null) => void;
}

interface StepSwitchProps extends FunnelStepRendererProps {
  isFetchingRecommendations: boolean;
  setIsFetchingRecommendations: (value: boolean) => void;
}

const StepSwitch: React.FC<StepSwitchProps> = ({
  isFetchingRecommendations,
  setIsFetchingRecommendations,
  funnelState,
  getLocalizedPath,
  isLoadingPrice,
  fetchBundleRecommendations,
  currentApiPrice,
  currentOffer,
  coveragePremiums,
  declinedSubs,
  coverageUnderwritingReasons,
  declinedCoverages,
  fetchCoverageRecommendation,
  underwritingFailed,
  underwritingReason,
  handlePetDetailsSubmitAndNavigate,
  handleSelectProductVariant,
  handleSelectProductVariantAndSkipToContact,
  handleProductVariantSelectionAndNavigate,
  handlePriceCustomizationSubmitAndNavigate,
  handleCreateApplication,
  handleContactDetailsContinue,
  handleContactDetailsFieldBlur,
  isSubmittingOffer,
  goToHome,
  goToPetDetails,
  goToProductVariantSelection,
  handleBackFromProductCustomization,
  handleBackFromContactDetails,
  goToProductCustomizationOptions,
  goToContactDetails,
  goToConfirmation,
  setSelectedProduct,
  setChipNumber,
  changeCoverageSelectionsOnComparison,
  setCoverageSelections,
  setSubCoverageSelections,
  setSubCoverageSumInsured,
  setCoverageDeductibles,
  setCoverageDeductiblesOtherBand,
  setCoverageSumInsured,
  setCoverageSumInsuredPerCase,
  setEmergencyCareSumInsuredPerDay,
  setAnnualDeductible,
  setDailyTermination,
  setBundleRecommendations,
  setPaymentInterval,
  setPaymentMethod,
  setStartOfInsurance,
  setFirstDue,
  onPetTypeChange,
  setDispatchContract,
  setDispatchInvoice,
  setDispatchReminder,
  setDispatchEmail,
  setAgentDispatchEmail,
  setCampaignCode,
  applicationAnswers,
  setApplicationAnswers,
  setConsultationProtocolDocument,
  clearConsultationProtocolDocument,
  setConsultationProtocolPending,
  clearConsultationProtocolPending,
  setBundlePrices,
}) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();

  const {
    petType,
    petName,
    breed,
    breedLabel,
    birthDate,
    selectedBreedType,
    gender,
    isCastrated,
    isHouseCat,
    selectedDogSize,
    chipNumber,
    preexistingConditionCategories,
    preexistingConditionComment,
    bundlePrices,
    bundleRecommendations,
    selectedProduct,
    finalPrice,
    contactFirstName, // Use contactFirstName directly
    contactLastName,  // Use contactLastName directly
    contactEmail,
    contactPhone,
    street,
    housenumber,
    city,
    zipCode,
    country,
    coverageSelections,
    subCoverageSelections,
    subCoverageSumInsured,
    coverageDeductibles,
    coverageDeductiblesOtherBand,
    coverageSumInsured,
    coverageSumInsuredPerCase,
    emergencyCareSumInsuredPerDay,
    annualDeductible,
    dailyTermination,
    paymentInterval,
    paymentMethod,
    startOfInsurance,
    agentId,
    dispatchContract,
    dispatchInvoice,
    dispatchReminder,
    dispatchEmail,
    agentDispatchEmail,
    campaignCode,
    contactBirthdate,
    customerGender,
  } = funnelState;
  const { hasSelectedPlan } = funnelState;

  const currentPath = location.pathname;

  if (
    currentPath === getLocalizedPath('routes.home') ||
    currentPath === getLocalizedPath('routes.petDetails')
  ) {
    return (
      <PetDetails
        petType={petType}
        onDetailsSubmit={async (...args) => {
          setIsFetchingRecommendations(true);
          try {
            await handlePetDetailsSubmitAndNavigate(...args);
          } finally {
            setIsFetchingRecommendations(false);
          }
        }}
        onBack={goToHome}
        initialPetName={petName}
        initialBreed={breed}
        initialBirthDate={birthDate}
        initialSelectedBreedType={selectedBreedType}
        initialGender={gender}
        initialIsCastrated={isCastrated}
        initialIsHouseCat={isHouseCat}
        initialSelectedDogSize={selectedDogSize}
        initialCoverageSelections={coverageSelections}
        initialStartOfInsurance={startOfInsurance}
        initialChipNumber={chipNumber}
        onPetTypeChange={onPetTypeChange}
      />
    );
  } else if (currentPath === getLocalizedPath('routes.productVariantSelection')) {
    return (
      <ProductVariantSelection
        petName={petName || t("common.yourPet")}
        bundleRecommendations={bundleRecommendations}
        coverageSelections={coverageSelections}
        onCoverageSelectionsChange={changeCoverageSelectionsOnComparison}
        petBirthDate={birthDate ?? null}
        petType={petType}
        onSelectAndNavigate={handleProductVariantSelectionAndNavigate}
        onSelectOnly={handleSelectProductVariant}
        onSelectAndSkipToContact={handleSelectProductVariantAndSkipToContact}
        onBack={goToPetDetails}
        selectedProduct={selectedProduct}
        isLoadingPrice={isLoadingPrice || isFetchingRecommendations}
        paymentInterval={paymentInterval}
        isFetchingRecommendations={isFetchingRecommendations || (!bundleRecommendations && isLoadingPrice)}
      />
    );
  } else if (currentPath === getLocalizedPath('routes.productCustomization')) {
    return (
      <ProductCustomizationOptions
        petName={petName || t("common.yourPet")}
        onCustomizeProduct={handlePriceCustomizationSubmitAndNavigate}
        onBack={handleBackFromProductCustomization}
        selectedProduct={selectedProduct}
        hasSelectedPlan={hasSelectedPlan}
        petBirthDate={birthDate ?? null}
        coverageSelections={coverageSelections}
        setCoverageSelections={setCoverageSelections}
        coverageDeductibles={coverageDeductibles}
        setCoverageDeductibles={setCoverageDeductibles}
        coverageDeductiblesOtherBand={coverageDeductiblesOtherBand}
        setCoverageDeductiblesOtherBand={setCoverageDeductiblesOtherBand}
        subCoverageSelections={subCoverageSelections}
        setSubCoverageSelections={setSubCoverageSelections}
        subCoverageSumInsured={subCoverageSumInsured}
        setSubCoverageSumInsured={setSubCoverageSumInsured}
        coverageSumInsured={coverageSumInsured}
        setCoverageSumInsured={setCoverageSumInsured}
        coverageSumInsuredPerCase={coverageSumInsuredPerCase}
        setCoverageSumInsuredPerCase={setCoverageSumInsuredPerCase}
        emergencyCareSumInsuredPerDay={emergencyCareSumInsuredPerDay}
        annualDeductible={annualDeductible}
        setEmergencyCareSumInsuredPerDay={setEmergencyCareSumInsuredPerDay}
        setAnnualDeductible={setAnnualDeductible}
        dailyTermination={dailyTermination}
        setDailyTermination={setDailyTermination}
        paymentInterval={paymentInterval}
        setPaymentInterval={setPaymentInterval}
        paymentMethod={paymentMethod}
        setPaymentMethod={setPaymentMethod}
        startOfInsurance={startOfInsurance}
        setStartOfInsurance={setStartOfInsurance}
        firstDue={funnelState.firstDue}
        setFirstDue={setFirstDue}
        currentApiPrice={currentApiPrice}
        coveragePremiums={coveragePremiums}
        dailyTerminationPremium={
          currentOffer?.dailyTermination?.selected && typeof currentOffer.dailyTermination.netPremiumProRata === "number"
            ? currentOffer.dailyTermination.netPremiumProRata + (currentOffer.dailyTermination.insuranceTaxProRata ?? 0)
            : null
        }
        declinedSubs={declinedSubs}
        coverageUnderwritingReasons={coverageUnderwritingReasons}
        declinedCoverages={declinedCoverages}
        fetchCoverageRecommendation={fetchCoverageRecommendation}
        isLoadingPrice={isLoadingPrice}
        underwritingFailed={underwritingFailed}
        underwritingReason={underwritingReason}
      />
    );
  } else if (currentPath === getLocalizedPath('routes.addressDetails')) {
    return (
      <ContactDetailsForm
        onContinueToSummary={handleContactDetailsContinue}
        onFieldBlur={handleContactDetailsFieldBlur}
        onBack={handleBackFromContactDetails}
        initialFirstName={contactFirstName || ""} // Pass directly
        initialLastName={contactLastName || ""}   // Pass directly
        initialEmail={contactEmail || ""}
        initialPhone={contactPhone || ""}
        initialStreet={street || ""}
        initialHousenumber={housenumber || ""}
        initialCity={city || ""}
        initialZipCode={zipCode || ""}
        initialBirthdate={contactBirthdate}
        initialGender={customerGender || ""}
        isLoading={isSubmittingOffer}
        initialDispatchContract={dispatchContract}
        initialDispatchInvoice={dispatchInvoice}
        initialDispatchReminder={dispatchReminder}
        initialDispatchEmail={dispatchEmail}
        initialAgentDispatchEmail={agentDispatchEmail || ""}
        initialCampaignCode={campaignCode}
        petName={petName || t("common.yourPet")}
        onDispatchContractChange={setDispatchContract}
        onDispatchInvoiceChange={setDispatchInvoice}
        onDispatchReminderChange={setDispatchReminder}
        onDispatchEmailChange={setDispatchEmail}
        onAgentDispatchEmailChange={setAgentDispatchEmail}
        onCampaignCodeChange={setCampaignCode}
        applicationAnswers={applicationAnswers}
        onApplicationAnswersChange={setApplicationAnswers}
        underwritingFailed={underwritingFailed}
        consultationProtocol={{
          contractId: funnelState.contractId,
          contractToken: funnelState.contractToken,
          isCopyMode: funnelState.offerLoadedFromUrl && funnelState.hasLeftConfirmationAfterOfferLoad,
          documentId: funnelState.consultationProtocolDocumentId,
          filename: funnelState.consultationProtocolFilename,
          pendingFile: funnelState.consultationProtocolPendingFile,
          pendingFilename: funnelState.consultationProtocolPendingFilename,
          onSetPending: setConsultationProtocolPending,
          onClearDocument: clearConsultationProtocolDocument,
          onClearPending: clearConsultationProtocolPending,
        }}
      />
    );
  } else if (currentPath === getLocalizedPath('routes.confirmation')) {
    if (!hasSelectedPlan || finalPrice === null) {
      return (
        <div className="w-full max-w-4xl mx-auto p-8 text-center">
          <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
          <div className="text-sm text-muted-foreground">Restoring your summary...</div>
        </div>
      );
    }
    return (
      <ConfirmationPage
        finalPrice={finalPrice!}
        // The offer behind the price; only after a reload (not persisted) the last calculation stands in.
        currentOffer={funnelState.confirmedOffer ?? currentOffer}
        contactFirstName={contactFirstName} // Pass directly
        contactLastName={contactLastName}   // Pass directly
        contactEmail={contactEmail!}
        contactPhone={contactPhone}
        addressLine1={street || ""}
        addressLine2={housenumber || null}
        city={city || ""}
        zipCode={zipCode || ""}
        country={country || ""}
        petType={petType || "dog"}
        petName={petName || ""}
        breed={breed}
        breedLabel={breedLabel}
        birthDate={birthDate!}
        selectedBreedType={selectedBreedType}
        gender={gender || "unknown"}
        isCastrated={isCastrated}
        isHouseCat={isHouseCat}
        coverageSelections={coverageSelections}
        declinedCoverages={declinedCoverages}
        subCoverageSelections={subCoverageSelections}
        subCoverageSumInsured={subCoverageSumInsured}
        coverageDeductibles={coverageDeductibles}
        coverageSumInsured={coverageSumInsured}
        coverageSumInsuredPerCase={coverageSumInsuredPerCase}
        emergencyCareSumInsuredPerDay={emergencyCareSumInsuredPerDay}
        annualDeductible={annualDeductible}
        paymentInterval={paymentInterval}
        paymentMethod={paymentMethod}
        startOfInsurance={startOfInsurance}
        firstDue={funnelState.firstDue}
        dailyTermination={dailyTermination}
        dispatchContract={dispatchContract}
        dispatchInvoice={dispatchInvoice}
        dispatchReminder={dispatchReminder}
        dispatchEmail={dispatchEmail}
        agentDispatchEmail={agentDispatchEmail}
        consultationProtocolDocumentId={funnelState.consultationProtocolDocumentId}
        consultationProtocolFilename={funnelState.consultationProtocolFilename}
        onStartNewQuote={goToHome}
        onBack={goToContactDetails}
        onApplyOffer={() =>
          handleCreateApplication(
            contactFirstName || "",
            contactLastName || "",
            contactEmail || "",
            contactPhone || "",
            street || "",
            housenumber || "",
            city || "",
            zipCode || "",
            country || ""
          )
        }
        isLoading={isSubmittingOffer}
        onEditPlanAndCoverage={() => goToProductCustomizationOptions()}
        onEditPetDetails={goToPetDetails}
        onEditPersonalDetails={goToContactDetails}
        onEditPayment={() => goToProductCustomizationOptions({ scrollTo: "payment" })}
        customerGender={funnelState.customerGender}
        contactBirthdate={contactBirthdate}
        selectedDogSize={selectedDogSize}
        chipNumber={chipNumber}
        onChipNumberChange={setChipNumber}
        preexistingConditionCategories={preexistingConditionCategories}
        preexistingConditionComment={preexistingConditionComment}
        campaignCode={campaignCode}
      />
    );
  }
  return (
    <PetDetails
      petType={petType}
      onDetailsSubmit={handlePetDetailsSubmitAndNavigate}
      onBack={goToHome}
      initialPetName={petName}
      initialBreed={breed}
      initialBirthDate={birthDate}
      initialSelectedBreedType={selectedBreedType}
      initialGender={gender}
      initialIsCastrated={isCastrated}
      initialIsHouseCat={isHouseCat}
      initialSelectedDogSize={selectedDogSize}
      initialCoverageSelections={coverageSelections}
      initialStartOfInsurance={startOfInsurance}
      initialChipNumber={chipNumber}
      onPetTypeChange={onPetTypeChange}
    />
  );
};

/** Renders the current step and dims the screen with the running-pet loader while the recommendations
 * are fetched (step 1 -> 2) or the offer is being created. */
const FunnelStepRenderer: React.FC<FunnelStepRendererProps> = (props) => {
  const [isFetchingRecommendations, setIsFetchingRecommendations] = React.useState(false);
  const location = useLocation();
  // The comparison page fetches the recommendations itself (first visit via offer link, or after the
  // coverage selection changed), and the premium query only reports loading until its first result.
  const isWaitingForRecommendations =
    location.pathname === props.getLocalizedPath('routes.productVariantSelection') && !props.funnelState.bundleRecommendations;
  const showLoader =
    isFetchingRecommendations || isWaitingForRecommendations || props.isSubmittingOffer || props.isLoadingPrice;
  return (
    <>
      {showLoader && <LoadingOverlay />}
      <StepSwitch
        {...props}
        isFetchingRecommendations={isFetchingRecommendations}
        setIsFetchingRecommendations={setIsFetchingRecommendations}
      />
    </>
  );
};

export default FunnelStepRenderer;
